import { Button, Cell, Grid, HFlow, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useErrorHandler } from 'components/error'
import {
  CidadaoAtendimentoSelectField,
  CidadaoAtendimentoSelectFieldModel,
  createDecorator,
  EquipeSelectField,
  Form,
  FormRenderProps,
  PecFormApi,
  resetAllFieldStates,
  SubmitButton,
} from 'components/form'
import { CidadaoAtendimentoPorPropriedadeSelectField } from 'components/form/field/select/CidadaoAtendimentoPorPropriedadeSelectField/CidadaoAtendimentoPorPropriedadeSelectField'
import { LotacaoAndEstagioSelectField } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { startOfDay } from 'date-fns'
import { useApolloClient } from 'graphql/hooks'
import { useAgendamentosDiaLazyQuery, useCidadaoLazyQuery, useSalvarAtendimentoMutation } from 'graphql/hooks.generated'
import { SituacaoAgendadoEnum, TipoEstabelecimentoEnum } from 'graphql/types.generated'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useServerTime } from 'hooks/useServerTime'
import { useCallback, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import { metaPath } from 'util/metaPath'
import { createValidator, required } from 'util/validation'
import { useSaudeIndigenaAuthorization } from 'view/cidadao/authorization/useSaudeIndigenaAuthorization'
import { removeCidadaoIdParam } from 'view/cidadao/utils-cidadao'
import { TipoServicoCheckField } from 'view/configuracoes/municipal/tipo-servico/TipoServicoCheckField'

import { AgendamentosDiaField } from '../agenda/AgendamentosDiaField'
import { convertNovoAtendimentoModelToInput } from '../converter'
import { createNovoAtendimentoCalculations } from '../detail/soap/finalizacao/calculator/calculatorNovoAtendimento'
import { EncaminhamentoInternoFormModel } from '../model-atendimento'
import { resolveEquipeTooltip, resolveProfissionalTooltip } from '../tooltipResolver'
import { cidadaoAusenteModal } from './modal/CidadaoAusenteModal'

export interface NovoAtendimentoFormModel extends EncaminhamentoInternoFormModel {
  cidadao?: CidadaoAtendimentoSelectFieldModel
}

const path = metaPath<NovoAtendimentoFormModel>()

const validate = createValidator<NovoAtendimentoFormModel>({
  cidadao: [required],
})

interface ListaAtendimentoFormProps {
  cidadaoId?: string
  updateFilterDefault: () => void
}

const cidadaoCalculator = {
  field: path.cidadao.absolutePath(),
  updates: {
    [path.agendado.absolutePath()]: (value, allValues: NovoAtendimentoFormModel) => {
      if (allValues.agendado) {
        return undefined
      }
    },
  },
}

export function ListaAtendimentoForm(props: ListaAtendimentoFormProps) {
  const { cidadaoId, updateFilterDefault } = props

  const apollo = useApolloClient()
  const alert = useAlert()
  const history = useHistory()
  const location = useLocation()
  const { acesso } = useAcessoLotacaoOrEstagio()
  const { hasCadastrarEditarAldeadoPermission } = useSaudeIndigenaAuthorization()
  const { getServerTimeNow } = useServerTime()
  const {
    configuracoes: { buscaCidadaoPorPropriedadesEnabled },
  } = useConfiguracoes()
  const handleRejection = useErrorHandler()

  const [salvarAtendimentoMutation, { loading }] = useSalvarAtendimentoMutation()
  const [executeCidadaoQuery, { data: cidadaoData }] = useCidadaoLazyQuery({ variables: { id: cidadaoId } })
  const [executeAgendamentosDiaQuery, { data: agendamentosDiaData }] = useAgendamentosDiaLazyQuery()

  const unidadeSaude = acesso?.unidadeSaude
  const tipoEstabelecimento = unidadeSaude?.tipoEstabelecimento

  const executeAgendamentosDiaCidadaoQuery = useCallback(
    (cidadaoId: ID) => {
      const startOfToday = startOfDay(getServerTimeNow())

      return executeAgendamentosDiaQuery({
        variables: {
          input: {
            cidadaoIds: [cidadaoId],
            dataAgendadoInicio: startOfToday,
            dataAgendadoFim: startOfToday,
            situacao: [SituacaoAgendadoEnum.AGENDADO],
            unidadeSaudeId: unidadeSaude.id,
            isForaDaUbs: false,
            isOrigemAtencaoDomiciliar: false,
          },
        },
      })
    },
    [executeAgendamentosDiaQuery, getServerTimeNow, unidadeSaude.id]
  )

  useEffect(() => {
    if (cidadaoId) {
      executeCidadaoQuery({ variables: { id: cidadaoId } })
      executeAgendamentosDiaCidadaoQuery(cidadaoId)
    }
  }, [cidadaoId, executeCidadaoQuery, executeAgendamentosDiaCidadaoQuery])

  const initialValues: NovoAtendimentoFormModel = useMemo(
    () => ({
      cidadao: cidadaoId && cidadaoData && { ...cidadaoData.cidadao, presenteListaAtendimento: false },
    }),
    [cidadaoData, cidadaoId]
  )

  const resetFormFields = (formApi: PecFormApi) => {
    resetAllFieldStates(formApi)
    formApi.reset({})
  }

  const clearExternalCidadaoSelection = () => {
    if (!cidadaoId) return

    history.replace({
      pathname: location.pathname,
      search: removeCidadaoIdParam(location.search) || undefined,
    })
  }

  const handleClear = (formApi: PecFormApi) => {
    clearExternalCidadaoSelection()
    resetFormFields(formApi)
  }

  const handleCidadaoChange = (newValue: CidadaoAtendimentoSelectFieldModel) => {
    if (!newValue || newValue.id !== cidadaoId) {
      clearExternalCidadaoSelection()
    }

    if (newValue && newValue.id) executeAgendamentosDiaCidadaoQuery(newValue.id)
  }

  const handleSubmit = async (value: NovoAtendimentoFormModel, formApi: PecFormApi) => {
    if (value.cidadao.isCidadaoAusente) {
      cidadaoAusenteModal({
        hasPermission: hasCadastrarEditarAldeadoPermission,
        onConfirm: () =>
          history.push(
            `${CIDADAO_PATH}/${value.cidadao.id}/edit?callbackUrl=lista-atendimento/cidadao-ausente&callbackParams=cidadaoId=${value.cidadao.id}`
          ),
      })
    } else if (!value.cidadao.dataNascimento) {
      alert(
        'danger',
        'Não é possível adicionar o cidadão. Atualize o cadastro do cidadão informando a data de nascimento.'
      )
    } else {
      save(value, formApi)
    }
  }

  const save = (value: NovoAtendimentoFormModel, formApi: PecFormApi) =>
    salvarAtendimentoMutation({
      variables: {
        input: convertNovoAtendimentoModelToInput(value),
      },
    })
      .then((res) => onSubmitSuccess(res, formApi))
      .catch(handleRejection)

  const onSubmitSuccess = (result, formApi: PecFormApi) => {
    alert('success', `Cidadão foi adicionado com sucesso.`)
    setTimeout(() => resetFormFields(formApi))
    updateFilterDefault()

    history.push('/lista-atendimento')
    return result
  }

  const renderForm = (formProps: FormRenderProps<NovoAtendimentoFormModel>) => {
    const values = formProps.values

    return (
      <Grid>
        <Cell size={5}>
          <VFlow>
            {buscaCidadaoPorPropriedadesEnabled ? (
              <CidadaoAtendimentoPorPropriedadeSelectField name={path.cidadao} onChange={handleCidadaoChange} />
            ) : (
              <CidadaoAtendimentoSelectField
                name={path.cidadao}
                ativo
                obito={false}
                label='Cidadão'
                onChange={handleCidadaoChange}
                addCidadaoCallbackUrl='/lista-atendimento'
                required
              />
            )}

            {values?.cidadao && (
              <AgendamentosDiaField
                name={path.agendado}
                agendamentosDia={values?.cidadao && agendamentosDiaData?.agendados?.content}
              />
            )}

            <Tooltip text={resolveProfissionalTooltip(values)}>
              <LotacaoAndEstagioSelectField
                name={path.responsavel}
                includeEstagios
                showNomeSocialLabel
                label='Profissional'
                unidadeSaudeId={unidadeSaude.id}
                disabled={(values?.equipe && !values?.responsavel) || !!values?.agendado}
              />
            </Tooltip>

            {tipoEstabelecimento !== TipoEstabelecimentoEnum.CEO && (
              <Tooltip text={resolveEquipeTooltip(values)}>
                <EquipeSelectField
                  name={path.equipe}
                  label='Equipe'
                  unidadeSaudeId={unidadeSaude.id}
                  disabled={!!values?.responsavel || !!values?.agendado}
                />
              </Tooltip>
            )}
          </VFlow>
        </Cell>

        <Cell size={7}>
          <div>
            <TipoServicoCheckField name={path.tiposServico} />
          </div>
        </Cell>

        <Cell alignSelf='flex-end' size={12}>
          <HFlow justifyContent='flex-end'>
            <Button size='medium' onClick={() => handleClear(formProps.form)}>
              Limpar campos
            </Button>
            <SubmitButton
              size='medium'
              loading={loading}
              disabled={loading}
              handleSubmit={formProps.handleSubmit}
              dataTestId='adicionarAtendimento'
            >
              Adicionar
            </SubmitButton>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  const decorator = useMemo(
    () => createDecorator(...createNovoAtendimentoCalculations(path, tipoEstabelecimento, apollo), cidadaoCalculator),
    [apollo, tipoEstabelecimento]
  )

  return (
    <Form<NovoAtendimentoFormModel>
      onSubmit={handleSubmit}
      render={renderForm}
      decorators={[decorator]}
      validate={validate}
      initialValues={initialValues}
    />
  )
}
